import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import EmailAutomationToggles from '../components/EmailAutomationToggles';
import {
  CreditCard, ArrowLeft, ClipboardPaste, Plus, Trash2, Send, Calendar,
  AlertCircle, CheckCircle2, Clock, Search, Mail, X, Loader2, ChevronDown,
  Edit2, Download, Bell, BellOff,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function statusBadge(status, days, manualRemoval) {
  if (status === 'red') {
    if (manualRemoval) {
      return <span data-testid="tw-badge-manual" className="inline-flex items-center gap-1 bg-red-600/30 border border-red-500/60 text-red-200 px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide">
        <AlertCircle className="w-3 h-3" /> Needs to be removed — call to cancel
      </span>;
    }
    return <span data-testid="tw-badge-red" className="inline-flex items-center gap-1 bg-red-600/30 border border-red-500/60 text-red-300 px-2 py-0.5 rounded-full text-[11px] font-bold">
      <AlertCircle className="w-3 h-3" /> NEEDS REMOVAL {days != null ? `(+${days}d)` : ''}
    </span>;
  }
  if (status === 'green') {
    return <span data-testid="tw-badge-green" className="inline-flex items-center gap-1 bg-emerald-600/20 border border-emerald-500/50 text-emerald-300 px-2 py-0.5 rounded-full text-[11px] font-semibold">
      <CheckCircle2 className="w-3 h-3" /> {days != null ? `${days}d left` : 'active'}
    </span>;
  }
  return <span data-testid="tw-badge-undated" className="inline-flex items-center gap-1 bg-zinc-700/50 border border-zinc-600 text-zinc-300 px-2 py-0.5 rounded-full text-[11px]">
    <Clock className="w-3 h-3" /> no date
  </span>;
}

function formatDateUS(iso) {
  if (!iso) return '—';
  // iso = YYYY-MM-DD → MM/DD/YYYY
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${m}/${d}/${y}`;
}

export default function TradelineWorldPage() {
  const [tab, setTab] = useState('pipeline'); // 'pipeline' | 'active'
  const [pipeline, setPipeline] = useState([]);
  const [groups, setGroups] = useState([]);
  const [totals, setTotals] = useState({ total: 0, green: 0, red: 0, undated: 0 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  // Pipeline paste/add
  const [pipelinePasteOpen, setPipelinePasteOpen] = useState(false);
  const [pipelinePasteText, setPipelinePasteText] = useState('');
  const [pipelinePreview, setPipelinePreview] = useState(null);

  // Active paste
  const [activePasteOpen, setActivePasteOpen] = useState(false);
  const [activePasteText, setActivePasteText] = useState('');
  const [activePastePreview, setActivePastePreview] = useState(null);

  // Per-pipeline-row "Add client" picker
  const [postingFor, setPostingFor] = useState(null); // pipeline client id
  const [postCardId, setPostCardId] = useState('');
  const [postAddDate, setPostAddDate] = useState(() => new Date().toISOString().slice(0, 10));

  // Active row inline editor (edit add_date)
  const [editingActiveId, setEditingActiveId] = useState(null);
  const [editingDate, setEditingDate] = useState('');

  // Add-card modal
  const [addCardOpen, setAddCardOpen] = useState(false);
  const [newCardName, setNewCardName] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, aRes] = await Promise.all([
        axios.get(`${API}/api/tradeline-world/pipeline`),
        axios.get(`${API}/api/tradeline-world/active`),
      ]);
      setPipeline(pRes.data.pipeline || []);
      setGroups(aRes.data.groups || []);
      setTotals(aRes.data.totals || { total: 0, green: 0, red: 0, undated: 0 });
    } catch (err) {
      console.error(err);
      toast.error('Failed to load Tradeline World');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const cards = useMemo(() => groups.map(g => g.card), [groups]);

  // Per-pipeline-client count of how many ACTIVE tradelines they're already
  // posted on. Per user request 2026-02-25 — we surface this number next to
  // their name in the Pending Additions list so the user can see at a glance
  // who's already on multiple cards before posting them again.
  const activeCountsByName = useMemo(() => {
    const counts = {};
    for (const g of groups) {
      for (const c of g.clients) {
        const key = (c.client_name || '').trim().toLowerCase();
        if (!key) continue;
        counts[key] = (counts[key] || 0) + 1;
      }
    }
    return counts;
  }, [groups]);

  const getActiveCount = (fullName) => activeCountsByName[(fullName || '').trim().toLowerCase()] || 0;

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups
      .map(g => ({
        ...g,
        clients: g.clients.filter(c =>
          (c.client_name || '').toLowerCase().includes(q) ||
          (g.card.name || '').toLowerCase().includes(q)
        ),
      }))
      .filter(g => g.clients.length > 0 || (g.card.name || '').toLowerCase().includes(q));
  }, [groups, search]);

  // Active list: cards are COLLAPSED by default — click a card header to expand
  // its client list. Per user request 2026-02-24 (revised): EVERY card starts
  // collapsed (no auto-expand for red cards) so the user gets a full overview
  // and can drill into whichever card they want.
  const [expandedCards, setExpandedCards] = useState({});
  // Search forces expansion of any card that has a match
  const searchActive = search.trim().length > 0;

  const toggleCardExpanded = (cardId) => {
    setExpandedCards(prev => ({ ...prev, [cardId]: !prev[cardId] }));
  };

  const expandAllCards = () => {
    const next = {};
    groups.forEach(g => { next[g.card.id] = true; });
    setExpandedCards(next);
  };
  const collapseAllCards = () => setExpandedCards({});

  // ── Pipeline paste flow ──
  const previewPipelinePaste = async () => {
    if (!pipelinePasteText.trim()) { toast.error('Paste some text first'); return; }
    try {
      const { data } = await axios.post(`${API}/api/tradeline-world/pipeline/paste`, {
        text: pipelinePasteText, confirm: false,
      });
      setPipelinePreview(data);
    } catch (err) { toast.error('Parse failed: ' + (err.response?.data?.detail || err.message)); }
  };
  const confirmPipelinePaste = async () => {
    try {
      const { data } = await axios.post(`${API}/api/tradeline-world/pipeline/paste`, {
        text: pipelinePasteText, confirm: true,
      });
      toast.success(`Added ${data.count} client(s) to pipeline`);
      setPipelinePasteOpen(false); setPipelinePasteText(''); setPipelinePreview(null);
      fetchAll();
    } catch (err) { toast.error('Save failed: ' + (err.response?.data?.detail || err.message)); }
  };

  // ── Active paste flow ──
  const previewActivePaste = async () => {
    if (!activePasteText.trim()) { toast.error('Paste some text first'); return; }
    try {
      const { data } = await axios.post(`${API}/api/tradeline-world/active/paste`, {
        text: activePasteText, default_year: new Date().getFullYear(), confirm: false,
      });
      setActivePastePreview(data);
    } catch (err) { toast.error('Parse failed: ' + (err.response?.data?.detail || err.message)); }
  };
  const confirmActivePaste = async () => {
    try {
      const { data } = await axios.post(`${API}/api/tradeline-world/active/paste`, {
        text: activePasteText, default_year: new Date().getFullYear(), confirm: true,
      });
      toast.success(`Saved ${data.client_count} client(s) across ${data.card_count} card(s)`);
      setActivePasteOpen(false); setActivePasteText(''); setActivePastePreview(null);
      fetchAll();
    } catch (err) { toast.error('Save failed: ' + (err.response?.data?.detail || err.message)); }
  };

  // ── Add pipeline client manually (quick single-row) ──
  const [quickName, setQuickName] = useState('');
  const [quickDob, setQuickDob] = useState('');
  const [quickSsn, setQuickSsn] = useState('');
  const [quickEmail, setQuickEmail] = useState('');
  const [quickQty, setQuickQty] = useState(1);

  // Smart paste handler — when the user pastes a multi-line block of client
  // info into the Name field (e.g. "Hector r Cortinas jr\n12-27-1987\n123-45-6789\nfoo@bar.com")
  // we auto-parse out the DOB, SSN and email and populate the other fields.
  // Per user request 2026-02-25 / 2026-02-26: pasting "Name DOB SSN" should
  // "just work" — 9-digit SSN → SSN field, MM/DD/YYYY date → DOB field, rest → Name.
  const handleQuickNamePaste = (e) => {
    const txt = (e.clipboardData || window.clipboardData)?.getData('text') || '';
    // Trigger auto-parse whenever any recognisable data-signal is present
    // (newlines, email, a date, or a 9-digit SSN — dashed or plain).
    if (!txt || !/[\n\r]|@|\d{1,2}[\/\-]\d{1,2}|\d{3}[-\s]?\d{2}[-\s]?\d{4}/.test(txt)) return;
    e.preventDefault();
    // Work on both the multi-line and single-line ("Name 12-27-1987 123-45-6789") pastes.
    const rawTokens = txt.split(/[\n\r]+/).map(l => l.trim()).filter(Boolean);
    let foundEmail = '';
    let foundDob = '';
    let foundSsn = '';
    const nameParts = [];
    const emailRe = /[\w.+-]+@[\w-]+\.[\w.-]+/;
    const dobRe = /(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/;
    // SSN: 9 digits, optionally dashed or space-separated (e.g. 123-45-6789 or 123456789)
    const ssnRe = /\b(\d{3})[-\s]?(\d{2})[-\s]?(\d{4})\b/;

    for (let ln of rawTokens) {
      // email
      const em = ln.match(emailRe);
      if (em && !foundEmail) { foundEmail = em[0]; ln = ln.replace(em[0], '').trim(); }
      // SSN — check BEFORE date so a 9-digit run isn't misread
      const sm = ln.match(ssnRe);
      if (sm && !foundSsn) {
        foundSsn = `${sm[1]}-${sm[2]}-${sm[3]}`;
        ln = ln.replace(sm[0], '').trim();
      }
      // DOB
      const dm = ln.match(dobRe);
      if (dm && !foundDob) {
        let [, m, d, y] = dm;
        if (y.length === 2) y = (parseInt(y, 10) >= 50 ? '19' : '20') + y;
        foundDob = `${m.padStart(2, '0')}/${d.padStart(2, '0')}/${y}`;
        ln = ln.replace(dm[0], '').trim();
      }
      // Anything left on the line is name material — strip stray commas / pipes.
      const cleaned = ln.replace(/[,|]+/g, ' ').replace(/\s+/g, ' ').trim();
      if (cleaned) nameParts.push(cleaned);
    }
    setQuickName(nameParts.join(' ').trim() || txt.trim());
    if (foundDob) setQuickDob(foundDob);
    if (foundSsn) setQuickSsn(foundSsn);
    if (foundEmail) setQuickEmail(foundEmail);
    const detected = [
      nameParts.join(' ') && 'name',
      foundDob && 'DOB',
      foundSsn && 'SSN',
      foundEmail && 'email',
    ].filter(Boolean).join(' · ');
    if (detected) toast.success('Auto-detected: ' + detected);
  };

  const addQuickPipeline = async () => {
    if (!quickName.trim()) { toast.error('Name required'); return; }
    try {
      await axios.post(`${API}/api/tradeline-world/pipeline`, {
        full_name: quickName, dob: quickDob, ssn: quickSsn, email: quickEmail,
        tradelines_purchased: Math.max(1, parseInt(quickQty || 1, 10)),
      });
      toast.success('Added to pipeline');
      setQuickName(''); setQuickDob(''); setQuickSsn(''); setQuickEmail(''); setQuickQty(1);
      fetchAll();
    } catch (err) { toast.error('Failed: ' + (err.response?.data?.detail || err.message)); }
  };

  const deletePipeline = async (id) => {
    if (!window.confirm('Remove this client from the pipeline?')) return;
    try { await axios.delete(`${API}/api/tradeline-world/pipeline/${id}`); fetchAll(); }
    catch (err) { toast.error('Failed'); }
  };

  // ── Inline edit for a pending-pipeline client (per user request 2026-02-25:
  // "I want to be able to edit any and everything"). The pencil icon on each
  // pending row opens a single-row form to update name/dob/ssn/email/qty.
  const [editingPipelineId, setEditingPipelineId] = useState(null);
  const [editPipelineForm, setEditPipelineForm] = useState(null);

  const startEditPipeline = (p) => {
    setEditingPipelineId(p.id);
    // Format DOB as MM/DD/YYYY for the input. We MUST NOT use `new Date()` on
    // a YYYY-MM-DD string — JS parses it as UTC midnight which shifts to the
    // previous day in any US timezone (so 2002-11-12 reads as 11/11/2002).
    // Build the string from the ISO parts directly instead.
    let dobDisplay = '';
    const rawDob = String(p.dob || '').trim();
    const isoMatch = rawDob.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      dobDisplay = `${isoMatch[2]}/${isoMatch[3]}/${isoMatch[1]}`;
    } else if (rawDob) {
      dobDisplay = rawDob;
    }
    setEditPipelineForm({
      full_name: p.full_name || '',
      dob: dobDisplay,
      ssn: p.ssn_last4 || '',
      email: p.email || '',
      tradelines_purchased: p.tradelines_purchased || 1,
    });
  };

  const saveEditPipeline = async () => {
    if (!editPipelineForm) return;
    if (!(editPipelineForm.full_name || '').trim()) { toast.error('Name required'); return; }
    // Coerce qty defensively — empty string, NaN, or null all fall back to 1.
    const qtyRaw = editPipelineForm.tradelines_purchased;
    let qty = parseInt(qtyRaw, 10);
    if (!Number.isFinite(qty) || qty < 1) qty = 1;
    try {
      await axios.put(`${API}/api/tradeline-world/pipeline/${editingPipelineId}`, {
        full_name: editPipelineForm.full_name,
        dob: editPipelineForm.dob,
        ssn: editPipelineForm.ssn,
        email: editPipelineForm.email,
        tradelines_purchased: qty,
      });
      toast.success('Pending client updated');
      setEditingPipelineId(null);
      setEditPipelineForm(null);
      fetchAll();
    } catch (err) {
      // Pydantic 422 returns `detail` as an array of {loc, msg, type}. Flatten
      // it to a readable string so the toast doesn't render "[object Object]".
      const raw = err.response?.data?.detail;
      let msg = 'Update failed';
      if (typeof raw === 'string') msg = raw;
      else if (Array.isArray(raw)) msg = raw.map(e => `${(e.loc || []).slice(-1)[0] || ''}: ${e.msg}`).join(' · ');
      toast.error(msg);
    }
  };

  const postClientToTradeline = async (clientId) => {
    if (!postCardId) { toast.error('Pick a tradeline first'); return; }
    try {
      await axios.post(`${API}/api/tradeline-world/pipeline/${clientId}/post`, {
        card_id: postCardId, add_date: postAddDate,
      });
      toast.success('Posted to tradeline ✓');
      setPostingFor(null); setPostCardId('');
      fetchAll();
    } catch (err) { toast.error('Failed: ' + (err.response?.data?.detail || err.message)); }
  };

  const deleteActive = async (id, name) => {
    if (!window.confirm(`Remove ${name || 'this client'} from the tradeline log?`)) return;
    try { await axios.delete(`${API}/api/tradeline-world/active/${id}`); fetchAll(); }
    catch (err) { toast.error('Failed'); }
  };

  const saveEditedDate = async (id) => {
    try {
      await axios.put(`${API}/api/tradeline-world/active/${id}`, { add_date: editingDate || null });
      setEditingActiveId(null); setEditingDate('');
      fetchAll();
    } catch (err) { toast.error('Failed to update'); }
  };

  const toggleManualRemoval = async (id, current) => {
    try {
      await axios.put(`${API}/api/tradeline-world/active/${id}`, { needs_removal_manual: !current });
      fetchAll();
    } catch (err) { toast.error('Failed to toggle'); }
  };

  // Per-client reminder silence toggle. Per user request 2026-02-08 —
  // "a simple button next to the client's name to turn on/off". Optimistic
  // update so the bell flips instantly; falls back to a full refetch if the
  // server rejects the change.
  // 2026-02-08 — Pipeline reminder toggle (Mon/Fri 8 AM Pacific digest).
  // Optimistic flip, rollback on error, per row.
  const togglePipelineReminder = async (id, currentEnabled, name) => {
    const next = !currentEnabled;
    setPipeline(prev => prev.map(p => p.id === id ? { ...p, reminder_enabled: next } : p));
    try {
      await axios.patch(`${API}/api/tradeline-world/pipeline/${id}/reminder`, null, {
        params: { enabled: next },
      });
      toast.success(next
        ? `${name || 'Client'} will appear in the Mon/Fri 8 AM digest`
        : `${name || 'Client'} silenced — no reminders`);
    } catch (err) {
      setPipeline(prev => prev.map(p => p.id === id ? { ...p, reminder_enabled: currentEnabled } : p));
      toast.error('Failed to toggle reminder');
    }
  };

  const toggleSilenceReminders = async (id, currentSilenced, name) => {
    const next = !currentSilenced;
    setGroups(prev => prev.map(g => ({
      ...g,
      clients: g.clients.map(c => c.id === id ? { ...c, reminders_silenced: next } : c),
    })));
    try {
      await axios.patch(`${API}/api/tradeline-world/active/${id}/silence`, null, {
        params: { silence: next },
      });
      toast.success(next
        ? `Reminders silenced for ${name || 'client'}`
        : `Reminders re-enabled for ${name || 'client'}`);
    } catch (err) {
      toast.error('Failed to toggle reminders');
      fetchAll();
    }
  };

  const addCard = async () => {
    if (!newCardName.trim()) { toast.error('Card name required'); return; }
    try {
      await axios.post(`${API}/api/tradeline-world/cards`, { name: newCardName.trim() });
      toast.success('Card added');
      setNewCardName(''); setAddCardOpen(false);
      fetchAll();
    } catch (err) { toast.error('Failed'); }
  };

  // ── Rename / delete an existing card. Per user request 2026-02-25 —
  // "I want to be able to edit the name of the Tradeline and remove the
  // line completely from the list." Renaming propagates to active clients
  // server-side; delete asks for explicit confirmation that includes the
  // current client count (and uses ?force=true to wipe them).
  const [renamingCardId, setRenamingCardId] = useState(null);
  const [renameCardValue, setRenameCardValue] = useState('');

  const startRenameCard = (cardId, currentName) => {
    setRenamingCardId(cardId);
    setRenameCardValue(currentName || '');
  };
  const saveRenameCard = async (cardId) => {
    const name = renameCardValue.trim();
    if (!name) { toast.error('Tradeline name required'); return; }
    try {
      await axios.put(`${API}/api/tradeline-world/cards/${cardId}`, { name });
      toast.success('Tradeline renamed');
      setRenamingCardId(null); setRenameCardValue('');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Rename failed'); }
  };
  const deleteCard = async (cardId, cardName, clientCount) => {
    let msg = `Delete the "${cardName}" tradeline from the log?`;
    if (clientCount > 0) {
      msg += `\n\nThis card currently has ${clientCount} client${clientCount === 1 ? '' : 's'} on it. They will be removed as well.`;
    }
    if (!window.confirm(msg)) return;
    try {
      const force = clientCount > 0 ? '?force=true' : '';
      const { data } = await axios.delete(`${API}/api/tradeline-world/cards/${cardId}${force}`);
      const removed = data.removed_clients || 0;
      toast.success(removed > 0 ? `Tradeline + ${removed} client(s) removed` : 'Tradeline removed');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  // ── Download the full active list as a Letter-size PDF (per user request
  // 2026-02-25). Grouped by tradeline name with add date + removal date. ──
  const [downloading, setDownloading] = useState(false);
  const downloadActivePdf = async () => {
    try {
      setDownloading(true);
      const resp = await axios.get(`${API}/api/tradeline-world/export`, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10);
      a.download = `tradelines-${ts}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Tradeline list downloaded');
    } catch (err) {
      toast.error('Download failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDownloading(false);
    }
  };

  // Per-card "Add client to this tradeline" inline form
  const [addingToCardId, setAddingToCardId] = useState(null);
  const [addClientForm, setAddClientForm] = useState({ name: '', add_date: '' });

  const submitAddClient = async (cardId) => {
    const name = (addClientForm.name || '').trim();
    if (!name) { toast.error('Client name required'); return; }
    try {
      const today = new Date().toISOString().slice(0, 10);
      await axios.post(`${API}/api/tradeline-world/active`, {
        card_id: cardId,
        client_name: name,
        add_date: addClientForm.add_date || today,
      });
      toast.success(`${name} added`);
      setAddingToCardId(null);
      setAddClientForm({ name: '', add_date: '' });
      fetchAll();
    } catch (err) { toast.error('Failed: ' + (err.response?.data?.detail || err.message)); }
  };

  const seedDefaults = async () => {
    if (!window.confirm('Add the saved tradeline list (17 cards, 58 clients)?\n\nThis is safe to run on any environment — anything already there is skipped.')) return;
    try {
      const { data } = await axios.post(`${API}/api/tradeline-world/seed-defaults`);
      toast.success(`Restored: ${data.cards_created} new card(s) + ${data.clients_created} client(s) (${data.clients_skipped} already there).`);
      fetchAll();
    } catch (err) { toast.error('Restore failed: ' + (err.response?.data?.detail || err.message)); }
  };

  const runSweep = async () => {
    if (!window.confirm('Send removal emails NOW for any client past 90 days?')) return;
    try {
      const { data } = await axios.post(`${API}/api/tradeline-world/run-removal-sweep`);
      if (data.success) {
        toast.success(`Sent ${data.sent} removal email(s)`);
        fetchAll();
      } else {
        toast.error(data.reason || 'Sweep failed');
      }
    } catch (err) { toast.error('Failed: ' + (err.response?.data?.detail || err.message)); }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <header className="bg-zinc-900 border-b border-zinc-800 px-4 py-3 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-zinc-400 hover:text-white" data-testid="tw-back-btn"><ArrowLeft className="w-5 h-5" /></Link>
            <div>
              <h1 className="text-lg font-bold flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-fuchsia-400" />
                Tradeline World
              </h1>
              <p className="text-[11px] text-zinc-500">90-day cycle · auto removal email at day 90 · all dates default to {new Date().getFullYear()}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              onClick={() => setAddCardOpen(true)}
              variant="outline"
              size="sm"
              className="border-fuchsia-700 text-fuchsia-300 hover:bg-fuchsia-900/30"
              data-testid="tw-header-add-card-btn"
              title="Add a new tradeline card"
            ><Plus className="w-3.5 h-3.5 mr-1.5" />New tradeline</Button>
            <Button
              onClick={runSweep}
              variant="outline"
              size="sm"
              className="border-amber-700 text-amber-300 hover:bg-amber-900/30"
              data-testid="tw-run-sweep-btn"
              title="Send removal emails right now for any client past 90 days"
            ><Send className="w-3.5 h-3.5 mr-1.5" />Send removal emails now</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-5">
        {/* Tab switcher with counts */}
        <div className="grid grid-cols-2 gap-2 mb-5 bg-zinc-900 border border-zinc-800 rounded-xl p-1.5 max-w-2xl">
          <button
            onClick={() => setTab('pipeline')}
            data-testid="tw-tab-pipeline"
            className={`flex items-center justify-center gap-2 py-2 px-2 rounded-lg text-sm font-semibold transition-colors ${tab === 'pipeline' ? 'bg-fuchsia-600 text-white shadow' : 'text-zinc-400 hover:text-white'}`}
          >
            <ClipboardPaste className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">Pending Additions</span>
            <span className="bg-black/30 px-2 py-0.5 rounded-full text-xs flex-shrink-0">{pipeline.length}</span>
          </button>
          <button
            onClick={() => setTab('active')}
            data-testid="tw-tab-active"
            className={`flex items-center justify-center gap-2 py-2 px-2 rounded-lg text-sm font-semibold transition-colors ${tab === 'active' ? 'bg-fuchsia-600 text-white shadow' : 'text-zinc-400 hover:text-white'}`}
          >
            <CreditCard className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">Active Tradelines</span>
            <span className="bg-black/30 px-2 py-0.5 rounded-full text-xs flex-shrink-0">{totals.total}</span>
            {totals.red > 0 && <span className="bg-red-500/30 border border-red-500/60 px-2 py-0.5 rounded-full text-xs text-red-200 flex-shrink-0">{totals.red}</span>}
          </button>
        </div>

        {/* ── Email Automations ── */}
        <div className="mb-5">
          <EmailAutomationToggles category="tradelines" />
        </div>

        {/* ── PIPELINE TAB ── */}
        {tab === 'pipeline' && (
          <div className="space-y-4">
            {/* Quick add row */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-bold text-zinc-200">Add a client to the pipeline</h2>
                <Button
                  onClick={() => setPipelinePasteOpen(true)}
                  variant="outline"
                  size="sm"
                  className="border-zinc-700 hover:bg-zinc-800"
                  data-testid="tw-pipeline-paste-btn"
                ><ClipboardPaste className="w-3.5 h-3.5 mr-1.5" />Paste many at once</Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_130px_120px_1fr_90px_auto] gap-2">
                <Input value={quickName} onChange={(e) => setQuickName(e.target.value)} onPaste={handleQuickNamePaste} placeholder="Full name * (paste a block to auto-split)" className="bg-zinc-800 border-zinc-700 text-white h-9 text-sm" data-testid="tw-quick-name" />
                <Input value={quickDob} onChange={(e) => setQuickDob(e.target.value)} placeholder="DOB (MM/DD/YYYY)" className="bg-zinc-800 border-zinc-700 text-white h-9 text-sm" data-testid="tw-quick-dob" />
                <Input value={quickSsn} onChange={(e) => setQuickSsn(e.target.value)} placeholder="SSN (optional)" className="bg-zinc-800 border-zinc-700 text-white h-9 text-sm font-mono" data-testid="tw-quick-ssn" />
                <Input value={quickEmail} onChange={(e) => setQuickEmail(e.target.value)} placeholder="Email" className="bg-zinc-800 border-zinc-700 text-white h-9 text-sm" data-testid="tw-quick-email" />
                <Input type="number" min={1} value={quickQty} onChange={(e) => setQuickQty(e.target.value)} placeholder="# TL" title="How many tradelines did the client purchase?" className="bg-zinc-800 border-zinc-700 text-white h-9 text-sm" data-testid="tw-quick-qty" />
                <Button onClick={addQuickPipeline} className="bg-fuchsia-600 hover:bg-fuchsia-700 h-9 px-4" data-testid="tw-quick-add-btn"><Plus className="w-4 h-4 mr-1" /><span>Add</span></Button>
              </div>
              <p className="text-[10px] text-zinc-500 mt-2"># TL = how many tradelines the client purchased (e.g. Lance bought 2). They'll stay in the pipeline until all slots are posted. SSN is <strong className="text-zinc-400">optional</strong> — only last 4 digits stored. Tip: paste a whole block (name + DOB + email) into the Name field and we'll auto-split.</p>
            </div>

            {/* Pipeline list */}
            {pipeline.length === 0 ? (
              <div className="bg-zinc-900 border border-dashed border-zinc-700 rounded-xl p-10 text-center text-zinc-500">
                Pipeline is empty. Add clients above or use "Paste many at once".
              </div>
            ) : (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden pb-20 lg:pb-0">
                {/* ── Desktop table (≥lg) ── SSN optional, only shown when present */}
                <table className="hidden lg:table w-full text-sm">
                  <thead className="bg-zinc-800/60 text-zinc-400 text-[11px] uppercase tracking-wide">
                    <tr>
                      <th className="text-left py-2 px-3">Name</th>
                      <th className="text-left py-2 px-3">DOB</th>
                      <th className="text-left py-2 px-3 font-mono">SSN ****</th>
                      <th className="text-left py-2 px-3">Email</th>
                      <th className="text-right py-2 px-3">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pipeline.map(p => (
                      <React.Fragment key={p.id}>
                        <tr className="border-t border-zinc-800 hover:bg-zinc-800/30" data-testid={`tw-pipeline-row-${p.id}`}>
                          <td className="py-2 px-3 font-medium text-white">
                            <div className="flex items-center gap-2 flex-wrap">
                              <button
                                onClick={() => togglePipelineReminder(p.id, p.reminder_enabled !== false, p.full_name)}
                                className={`flex-shrink-0 p-1 rounded-full transition-colors ${
                                  (p.reminder_enabled === false)
                                    ? 'text-zinc-600 hover:text-zinc-400 bg-zinc-900'
                                    : 'text-emerald-400 hover:text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20'
                                }`}
                                title={(p.reminder_enabled === false)
                                  ? 'Reminder off — click to include in Mon/Fri 8 AM digest'
                                  : 'Reminder on — this client is in the Mon/Fri 8 AM digest'}
                                aria-label={(p.reminder_enabled === false) ? 'Enable reminder' : 'Silence reminder'}
                                data-testid={`tw-pipeline-reminder-${p.id}`}
                              >
                                {(p.reminder_enabled === false)
                                  ? <BellOff className="w-3.5 h-3.5" />
                                  : <Bell className="w-3.5 h-3.5" />}
                              </button>
                              <span>{p.full_name}</span>
                            </div>
                            {(p.tradelines_purchased || 1) > 1 && (
                              <span
                                className="ml-2 inline-flex items-center gap-1 bg-amber-500/20 border border-amber-500/50 text-amber-200 px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide"
                                title={`Client purchased ${p.tradelines_purchased} tradelines — they'll stay in pipeline until all are posted`}
                                data-testid={`tw-pipeline-purchased-${p.id}`}
                              >
                                bought {p.tradelines_purchased}
                              </span>
                            )}
                            {getActiveCount(p.full_name) > 0 && (
                              <span
                                className="ml-2 inline-flex items-center gap-1 bg-fuchsia-500/20 border border-fuchsia-500/50 text-fuchsia-200 px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide"
                                title={`Already on ${getActiveCount(p.full_name)} active tradeline(s)`}
                                data-testid={`tw-pipeline-active-count-${p.id}`}
                              >
                                <CreditCard className="w-2.5 h-2.5" />
                                on {getActiveCount(p.full_name)} tradeline{getActiveCount(p.full_name) === 1 ? '' : 's'}
                              </span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-zinc-300">{formatDateUS(p.dob) || '—'}</td>
                          <td className="py-2 px-3 text-zinc-400 font-mono text-xs">{p.ssn_last4 ? `***-**-${p.ssn_last4}` : '—'}</td>
                          <td className="py-2 px-3 text-zinc-300">{p.email || '—'}</td>
                          <td className="py-2 px-3 text-right">
                            <div className="inline-flex gap-1">
                              <Button
                                onClick={() => { setPostingFor(postingFor === p.id ? null : p.id); setPostCardId(''); setPostAddDate(new Date().toISOString().slice(0, 10)); }}
                                size="sm"
                                className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs"
                                data-testid={`tw-pipeline-add-${p.id}`}
                              >Add client to tradeline</Button>
                              <Button
                                onClick={() => startEditPipeline(p)}
                                size="sm"
                                variant="outline"
                                className="border-cyan-700/40 text-cyan-200 hover:bg-cyan-900/30 h-7 px-2"
                                data-testid={`tw-pipeline-edit-${p.id}`}
                                title="Edit this pending client"
                              ><Edit2 className="w-3 h-3" /></Button>
                              <Button
                                onClick={() => deletePipeline(p.id)}
                                size="sm"
                                variant="outline"
                                className="border-zinc-700 h-7 px-2"
                                data-testid={`tw-pipeline-delete-${p.id}`}
                              ><Trash2 className="w-3 h-3" /></Button>
                            </div>
                          </td>
                        </tr>
                        {editingPipelineId === p.id && (
                          <tr className="bg-cyan-950/40 border-t border-cyan-700/40">
                            <td colSpan={5} className="px-3 py-3">
                              <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px_120px_1fr_90px_auto] gap-2">
                                <Input value={editPipelineForm.full_name} onChange={(e) => setEditPipelineForm({ ...editPipelineForm, full_name: e.target.value })} placeholder="Full name" className="bg-zinc-900 border-cyan-700/60 text-white h-8 text-sm" data-testid={`tw-pipeline-edit-name-${p.id}`} />
                                <Input value={editPipelineForm.dob} onChange={(e) => setEditPipelineForm({ ...editPipelineForm, dob: e.target.value })} placeholder="DOB" className="bg-zinc-900 border-cyan-700/60 text-white h-8 text-sm" data-testid={`tw-pipeline-edit-dob-${p.id}`} />
                                <Input value={editPipelineForm.ssn} onChange={(e) => setEditPipelineForm({ ...editPipelineForm, ssn: e.target.value })} placeholder="SSN (optional)" className="bg-zinc-900 border-cyan-700/60 text-white h-8 text-sm font-mono" data-testid={`tw-pipeline-edit-ssn-${p.id}`} />
                                <Input value={editPipelineForm.email} onChange={(e) => setEditPipelineForm({ ...editPipelineForm, email: e.target.value })} placeholder="Email" className="bg-zinc-900 border-cyan-700/60 text-white h-8 text-sm" data-testid={`tw-pipeline-edit-email-${p.id}`} />
                                <Input type="number" min={1} value={editPipelineForm.tradelines_purchased} onChange={(e) => setEditPipelineForm({ ...editPipelineForm, tradelines_purchased: e.target.value })} placeholder="# TL" className="bg-zinc-900 border-cyan-700/60 text-white h-8 text-sm" data-testid={`tw-pipeline-edit-qty-${p.id}`} />
                                <div className="flex gap-1">
                                  <Button onClick={saveEditPipeline} className="bg-cyan-600 hover:bg-cyan-700 h-8 text-xs flex-1" data-testid={`tw-pipeline-edit-save-${p.id}`}>Save</Button>
                                  <Button onClick={() => { setEditingPipelineId(null); setEditPipelineForm(null); }} variant="outline" size="sm" className="border-zinc-700 h-8 text-xs">Cancel</Button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                        {postingFor === p.id && (
                          <tr className="bg-emerald-950/40 border-t border-emerald-700/40">
                            <td colSpan={5} className="px-3 py-3">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-emerald-300 text-xs font-semibold">Post to →</span>
                                <select
                                  value={postCardId}
                                  onChange={(e) => setPostCardId(e.target.value)}
                                  className="bg-zinc-900 border border-emerald-700/60 rounded px-2 py-1 text-sm text-white"
                                  data-testid={`tw-pipeline-card-select-${p.id}`}
                                >
                                  <option value="">— pick a tradeline —</option>
                                  {cards.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                                <span className="text-emerald-300 text-xs">Date added:</span>
                                <Input
                                  type="date"
                                  value={postAddDate}
                                  onChange={(e) => setPostAddDate(e.target.value)}
                                  className="bg-zinc-900 border-emerald-700/60 text-white h-8 text-sm w-40"
                                  data-testid={`tw-pipeline-date-${p.id}`}
                                />
                                <Button onClick={() => postClientToTradeline(p.id)} className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs" data-testid={`tw-pipeline-post-confirm-${p.id}`}>Post</Button>
                                <Button onClick={() => setPostingFor(null)} variant="outline" size="sm" className="border-zinc-700 h-8 text-xs">Cancel</Button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>

                {/* ── Mobile card layout (<lg) — full client info visible at a
                    glance + delete button on the LEFT so the Emergent
                    bottom-right badge can never block it. Per user request
                    2026-02-25: "I always need to be able to see everything
                    on the screen." ── */}
                <div className="lg:hidden divide-y divide-zinc-800">
                  {pipeline.map(p => (
                    <div key={p.id} className="p-3 space-y-2" data-testid={`tw-pipeline-card-${p.id}`}>
                      <div className="flex items-start gap-2">
                        {/* Trash on the LEFT so the bottom-right "Made with Emergent" badge can't cover it */}
                        <Button
                          onClick={() => deletePipeline(p.id)}
                          size="sm"
                          variant="outline"
                          className="border-red-700/40 text-red-300 hover:bg-red-900/30 h-9 w-9 p-0 flex-shrink-0"
                          data-testid={`tw-pipeline-delete-mobile-${p.id}`}
                          aria-label="Delete client"
                        ><Trash2 className="w-4 h-4" /></Button>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => togglePipelineReminder(p.id, p.reminder_enabled !== false, p.full_name)}
                              className={`flex-shrink-0 p-1 rounded-full transition-colors ${
                                (p.reminder_enabled === false)
                                  ? 'text-zinc-600 hover:text-zinc-400 bg-zinc-900'
                                  : 'text-emerald-400 hover:text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20'
                              }`}
                              title={(p.reminder_enabled === false)
                                ? 'Reminder off — tap to include in Mon/Fri digest'
                                : 'In Mon/Fri 8 AM digest — tap to silence'}
                              aria-label={(p.reminder_enabled === false) ? 'Enable reminder' : 'Silence reminder'}
                              data-testid={`tw-pipeline-reminder-mobile-${p.id}`}
                            >
                              {(p.reminder_enabled === false)
                                ? <BellOff className="w-3.5 h-3.5" />
                                : <Bell className="w-3.5 h-3.5" />}
                            </button>
                            <p className="text-white font-semibold text-sm break-words leading-tight">{p.full_name}</p>
                          </div>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {(p.tradelines_purchased || 1) > 1 && (
                              <span
                                className="inline-flex items-center gap-1 bg-amber-500/20 border border-amber-500/50 text-amber-200 px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide"
                                data-testid={`tw-pipeline-purchased-mobile-${p.id}`}
                              >
                                bought {p.tradelines_purchased}
                              </span>
                            )}
                            {getActiveCount(p.full_name) > 0 && (
                              <span
                                className="inline-flex items-center gap-1 bg-fuchsia-500/20 border border-fuchsia-500/50 text-fuchsia-200 px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide"
                                data-testid={`tw-pipeline-active-count-mobile-${p.id}`}
                              >
                                <CreditCard className="w-2.5 h-2.5" />
                                on {getActiveCount(p.full_name)} tradeline{getActiveCount(p.full_name) === 1 ? '' : 's'}
                              </span>
                            )}
                          </div>
                          <p className="text-zinc-400 text-xs mt-0.5">
                            <span className="text-zinc-500 uppercase tracking-wide text-[10px] mr-1">DOB</span>
                            {formatDateUS(p.dob) || '—'}
                          </p>
                          {p.ssn_last4 && (
                            <p className="text-zinc-400 text-xs font-mono">
                              <span className="text-zinc-500 uppercase tracking-wide text-[10px] mr-1 font-sans">SSN</span>
                              ***-**-{p.ssn_last4}
                            </p>
                          )}
                          <p className="text-zinc-400 text-xs break-all">
                            <span className="text-zinc-500 uppercase tracking-wide text-[10px] mr-1">Email</span>
                            {p.email || '—'}
                          </p>
                        </div>
                        {/* Edit button next to trash — full edit on click */}
                        <Button
                          onClick={() => startEditPipeline(p)}
                          size="sm"
                          variant="outline"
                          className="border-cyan-700/40 text-cyan-200 hover:bg-cyan-900/30 h-9 w-9 p-0 flex-shrink-0"
                          data-testid={`tw-pipeline-edit-mobile-${p.id}`}
                          aria-label="Edit client"
                        ><Edit2 className="w-4 h-4" /></Button>
                      </div>
                      {editingPipelineId === p.id && editPipelineForm && (
                        <div className="bg-cyan-950/30 border border-cyan-700/40 rounded-lg p-3 space-y-2">
                          <div>
                            <label className="text-cyan-300 text-[10px] uppercase tracking-wide mb-1 block">Full name</label>
                            <Input value={editPipelineForm.full_name} onChange={(e) => setEditPipelineForm({ ...editPipelineForm, full_name: e.target.value })} className="w-full bg-zinc-900 border-cyan-700/60 text-white h-9 text-sm" data-testid={`tw-pipeline-edit-name-mobile-${p.id}`} />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="text-cyan-300 text-[10px] uppercase tracking-wide mb-1 block">DOB</label>
                              <Input value={editPipelineForm.dob} onChange={(e) => setEditPipelineForm({ ...editPipelineForm, dob: e.target.value })} placeholder="MM/DD/YYYY" className="w-full bg-zinc-900 border-cyan-700/60 text-white h-9 text-sm" data-testid={`tw-pipeline-edit-dob-mobile-${p.id}`} />
                            </div>
                            <div>
                              <label className="text-cyan-300 text-[10px] uppercase tracking-wide mb-1 block">SSN (optional)</label>
                              <Input value={editPipelineForm.ssn} onChange={(e) => setEditPipelineForm({ ...editPipelineForm, ssn: e.target.value })} className="w-full bg-zinc-900 border-cyan-700/60 text-white h-9 text-sm font-mono" data-testid={`tw-pipeline-edit-ssn-mobile-${p.id}`} />
                            </div>
                          </div>
                          <div>
                            <label className="text-cyan-300 text-[10px] uppercase tracking-wide mb-1 block">Email</label>
                            <Input value={editPipelineForm.email} onChange={(e) => setEditPipelineForm({ ...editPipelineForm, email: e.target.value })} className="w-full bg-zinc-900 border-cyan-700/60 text-white h-9 text-sm" data-testid={`tw-pipeline-edit-email-mobile-${p.id}`} />
                          </div>
                          <div>
                            <label className="text-cyan-300 text-[10px] uppercase tracking-wide mb-1 block"># of tradelines purchased</label>
                            <Input type="number" min={1} value={editPipelineForm.tradelines_purchased} onChange={(e) => setEditPipelineForm({ ...editPipelineForm, tradelines_purchased: e.target.value })} className="w-full bg-zinc-900 border-cyan-700/60 text-white h-9 text-sm" data-testid={`tw-pipeline-edit-qty-mobile-${p.id}`} />
                          </div>
                          <div className="flex gap-2">
                            <Button onClick={saveEditPipeline} className="flex-1 bg-cyan-600 hover:bg-cyan-700 h-9 text-sm" data-testid={`tw-pipeline-edit-save-mobile-${p.id}`}>Save changes</Button>
                            <Button onClick={() => { setEditingPipelineId(null); setEditPipelineForm(null); }} variant="outline" size="sm" className="border-zinc-700 h-9 text-sm">Cancel</Button>
                          </div>
                        </div>
                      )}
                      <Button
                        onClick={() => { setPostingFor(postingFor === p.id ? null : p.id); setPostCardId(''); setPostAddDate(new Date().toISOString().slice(0, 10)); }}
                        className="w-full bg-emerald-600 hover:bg-emerald-700 h-9 text-sm"
                        data-testid={`tw-pipeline-add-mobile-${p.id}`}
                      >Add client to tradeline</Button>
                      {postingFor === p.id && (
                        <div className="bg-emerald-950/40 border border-emerald-700/40 rounded-lg p-3 space-y-2">
                          <div>
                            <p className="text-emerald-300 text-[10px] uppercase tracking-wide mb-1">Post to tradeline</p>
                            <select
                              value={postCardId}
                              onChange={(e) => setPostCardId(e.target.value)}
                              className="w-full bg-zinc-900 border border-emerald-700/60 rounded px-2 py-1.5 text-sm text-white"
                              data-testid={`tw-pipeline-card-select-mobile-${p.id}`}
                            >
                              <option value="">— pick a tradeline —</option>
                              {cards.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                          </div>
                          <div>
                            <p className="text-emerald-300 text-[10px] uppercase tracking-wide mb-1">Date added</p>
                            <Input
                              type="date"
                              value={postAddDate}
                              onChange={(e) => setPostAddDate(e.target.value)}
                              className="w-full bg-zinc-900 border-emerald-700/60 text-white h-9 text-sm"
                              data-testid={`tw-pipeline-date-mobile-${p.id}`}
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button onClick={() => postClientToTradeline(p.id)} className="flex-1 bg-emerald-600 hover:bg-emerald-700 h-9 text-sm" data-testid={`tw-pipeline-post-confirm-mobile-${p.id}`}>Post</Button>
                            <Button onClick={() => setPostingFor(null)} variant="outline" size="sm" className="border-zinc-700 h-9 text-sm">Cancel</Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── ACTIVE TAB ── */}
        {tab === 'active' && (
          <div className="space-y-4">
            {/* Stat ribbon */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-3"><p className="text-[10px] text-zinc-500 uppercase">Total</p><p className="text-2xl font-bold text-white">{totals.total}</p></div>
              <div className="bg-emerald-950/40 border border-emerald-700/40 rounded-lg p-3"><p className="text-[10px] text-emerald-400 uppercase">Active 🟢</p><p className="text-2xl font-bold text-emerald-300">{totals.green}</p></div>
              <div className="bg-red-950/40 border border-red-700/40 rounded-lg p-3"><p className="text-[10px] text-red-400 uppercase">Need removal 🔴</p><p className="text-2xl font-bold text-red-300">{totals.red}</p></div>
              <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-3"><p className="text-[10px] text-zinc-400 uppercase">No date</p><p className="text-2xl font-bold text-zinc-300">{totals.undated}</p></div>
            </div>

            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by client or tradeline…"
                  className="bg-zinc-900 border-zinc-700 text-white h-9 pl-8 text-sm"
                  data-testid="tw-active-search"
                />
              </div>
              <Button
                onClick={() => setActivePasteOpen(true)}
                variant="outline"
                size="sm"
                className="border-zinc-700 hover:bg-zinc-800"
                data-testid="tw-active-paste-btn"
              ><ClipboardPaste className="w-3.5 h-3.5 mr-1.5" />Paste many at once</Button>
              <Button
                onClick={() => setAddCardOpen(true)}
                variant="outline"
                size="sm"
                className="border-zinc-700 hover:bg-zinc-800"
                data-testid="tw-add-card-btn"
              ><Plus className="w-3.5 h-3.5 mr-1.5" />Add tradeline card</Button>
              <Button
                onClick={downloadActivePdf}
                disabled={downloading}
                variant="outline"
                size="sm"
                className="border-cyan-700/60 hover:bg-cyan-900/30 text-cyan-200"
                data-testid="tw-download-list-btn"
              >{downloading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}Download list</Button>
              <Button
                onClick={expandAllCards}
                variant="outline"
                size="sm"
                className="border-zinc-700 hover:bg-zinc-800 text-xs"
                data-testid="tw-expand-all-btn"
              >Expand all</Button>
              <Button
                onClick={collapseAllCards}
                variant="outline"
                size="sm"
                className="border-zinc-700 hover:bg-zinc-800 text-xs"
                data-testid="tw-collapse-all-btn"
              >Collapse all</Button>
            </div>

            {/* Cards */}
            {filteredGroups.length === 0 && !loading && (
              <div className="bg-zinc-900 border border-dashed border-zinc-700 rounded-xl p-10 text-center">
                <p className="text-zinc-400 mb-4">No tradelines yet.</p>
                <div className="flex flex-wrap justify-center gap-2">
                  <Button onClick={seedDefaults} className="bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="tw-restore-default-btn">
                    Restore my 17 cards / 58 clients
                  </Button>
                  <Button onClick={() => setActivePasteOpen(true)} variant="outline" className="border-zinc-700">
                    Or paste a new list
                  </Button>
                </div>
                <p className="text-[11px] text-zinc-600 mt-3">"Restore" is safe — anything already there is skipped, nothing is overwritten.</p>
              </div>
            )}

            {filteredGroups.map(g => {
              // EVERY card starts collapsed. Only auto-open if:
              //   • user explicitly clicked to expand, OR
              //   • search is active (so matches aren't hidden)
              // Red cards no longer force-open — the user wants a full overview.
              const isExpanded = expandedCards[g.card.id] || searchActive;
              return (
              <div key={g.card.id} className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden" data-testid={`tw-card-${g.card.id}`}>
                <button
                  type="button"
                  onClick={() => toggleCardExpanded(g.card.id)}
                  className="w-full flex flex-wrap items-center justify-between px-3 sm:px-4 py-3 bg-zinc-800/60 border-b border-zinc-700 gap-x-2 gap-y-2 hover:bg-zinc-800/80 transition-colors text-left"
                  data-testid={`tw-card-header-${g.card.id}`}
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1 basis-full sm:basis-auto">
                    <ChevronDown className={`w-4 h-4 text-zinc-500 flex-shrink-0 transition-transform ${isExpanded ? '' : '-rotate-90'}`} />
                    <CreditCard className="w-4 h-4 text-fuchsia-400 flex-shrink-0" />
                    {renamingCardId === g.card.id ? (
                      <div className="flex items-center gap-1 flex-1 min-w-0" onClick={(e) => e.stopPropagation()}>
                        <Input
                          autoFocus
                          value={renameCardValue}
                          onChange={(e) => setRenameCardValue(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); saveRenameCard(g.card.id); }
                            if (e.key === 'Escape') { e.preventDefault(); setRenamingCardId(null); }
                          }}
                          className="bg-zinc-900 border-fuchsia-700/60 text-white h-7 text-sm flex-1 min-w-0"
                          data-testid={`tw-card-rename-input-${g.card.id}`}
                        />
                        <Button
                          onClick={(e) => { e.stopPropagation(); saveRenameCard(g.card.id); }}
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700 h-7 px-2 flex-shrink-0"
                          data-testid={`tw-card-rename-save-${g.card.id}`}
                          aria-label="Save name"
                        ><CheckCircle2 className="w-3 h-3" /></Button>
                        <Button
                          onClick={(e) => { e.stopPropagation(); setRenamingCardId(null); setRenameCardValue(''); }}
                          size="sm"
                          variant="outline"
                          className="border-zinc-700 h-7 px-2 flex-shrink-0"
                          aria-label="Cancel rename"
                        ><X className="w-3 h-3" /></Button>
                      </div>
                    ) : (
                      <>
                        <h3 className="text-sm font-bold text-white break-words flex-1 min-w-0">{g.card.name}</h3>
                        <span className="text-[11px] text-zinc-400 flex-shrink-0">· {g.total}</span>
                        {g.red_count > 0 && <span className="bg-red-500/30 border border-red-500/60 text-red-200 text-[10px] px-2 py-0.5 rounded-full font-semibold flex-shrink-0">{g.red_count} red</span>}
                        {g.green_count > 0 && <span className="bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-[10px] px-2 py-0.5 rounded-full font-semibold flex-shrink-0">{g.green_count} active</span>}
                      </>
                    )}
                  </div>
                  {renamingCardId !== g.card.id && (
                    <div className="flex items-center gap-1 flex-shrink-0 ml-auto">
                      {/* Rename tradeline */}
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); startRenameCard(g.card.id, g.card.name); }}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); startRenameCard(g.card.id, g.card.name); } }}
                        className="inline-flex items-center justify-center h-7 w-7 rounded bg-zinc-700/60 hover:bg-zinc-600 text-zinc-200 cursor-pointer transition-colors"
                        data-testid={`tw-card-rename-btn-${g.card.id}`}
                        title="Rename this tradeline"
                        aria-label="Rename tradeline"
                      ><Edit2 className="w-3 h-3" /></span>
                      {/* Delete tradeline */}
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); deleteCard(g.card.id, g.card.name, g.total); }}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); deleteCard(g.card.id, g.card.name, g.total); } }}
                        className="inline-flex items-center justify-center h-7 w-7 rounded bg-red-900/40 hover:bg-red-800/60 text-red-200 cursor-pointer transition-colors"
                        data-testid={`tw-card-delete-btn-${g.card.id}`}
                        title="Delete this tradeline (and all clients on it)"
                        aria-label="Delete tradeline"
                      ><Trash2 className="w-3 h-3" /></span>
                      {/* Add client (existing) */}
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          setAddingToCardId(addingToCardId === g.card.id ? null : g.card.id);
                          setAddClientForm({ name: '', add_date: new Date().toISOString().slice(0, 10) });
                          if (!isExpanded) setExpandedCards(prev => ({ ...prev, [g.card.id]: true }));
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            setAddingToCardId(addingToCardId === g.card.id ? null : g.card.id);
                            setAddClientForm({ name: '', add_date: new Date().toISOString().slice(0, 10) });
                            if (!isExpanded) setExpandedCards(prev => ({ ...prev, [g.card.id]: true }));
                          }
                        }}
                        className="inline-flex items-center justify-center bg-emerald-600 hover:bg-emerald-700 h-7 px-2 text-xs rounded text-white font-medium cursor-pointer"
                        data-testid={`tw-add-client-btn-${g.card.id}`}
                      ><Plus className="w-3 h-3 mr-0.5" />Add client</span>
                    </div>
                  )}
                </button>
                {isExpanded && addingToCardId === g.card.id && (
                  <div className="bg-emerald-950/30 border-b border-emerald-700/40 px-3 py-2.5">
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Input
                        value={addClientForm.name}
                        onChange={(e) => setAddClientForm({ ...addClientForm, name: e.target.value })}
                        placeholder="Client name"
                        className="bg-zinc-900 border-emerald-700/60 text-white h-8 text-sm flex-1"
                        autoFocus
                        data-testid={`tw-add-client-name-${g.card.id}`}
                      />
                      <Input
                        type="date"
                        value={addClientForm.add_date}
                        onChange={(e) => setAddClientForm({ ...addClientForm, add_date: e.target.value })}
                        className="bg-zinc-900 border-emerald-700/60 text-white h-8 text-sm sm:w-40"
                        data-testid={`tw-add-client-date-${g.card.id}`}
                      />
                      <div className="flex gap-2">
                        <Button onClick={() => submitAddClient(g.card.id)} className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs flex-1 sm:flex-initial" data-testid={`tw-add-client-save-${g.card.id}`}>Add</Button>
                        <Button onClick={() => setAddingToCardId(null)} variant="outline" size="sm" className="border-zinc-700 h-8 text-xs">Cancel</Button>
                      </div>
                    </div>
                    <p className="text-[10px] text-emerald-400/80 mt-1.5">Date defaults to today · 90-day clock starts from this date</p>
                  </div>
                )}
                {isExpanded && (g.clients.length === 0 ? (
                  <div className="px-4 py-3 text-xs text-zinc-500 italic">No clients on this card yet.</div>
                ) : (
                  <div className="divide-y divide-zinc-800">
                    {g.clients.map(c => (
                      <div key={c.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-4 py-2.5 text-sm hover:bg-zinc-800/40" data-testid={`tw-client-${c.id}`}>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 flex-1 min-w-0">
                          <div className="flex items-center gap-2 min-w-0">
                            <button
                              onClick={() => toggleSilenceReminders(c.id, c.reminders_silenced, c.client_name)}
                              className={`flex-shrink-0 p-1 rounded-full transition-colors ${
                                c.reminders_silenced
                                  ? 'text-amber-400 hover:text-amber-300 bg-amber-500/10 hover:bg-amber-500/20'
                                  : 'text-zinc-500 hover:text-emerald-400 hover:bg-zinc-800'
                              }`}
                              title={c.reminders_silenced
                                ? 'Reminders silenced — click to re-enable'
                                : 'Reminders on — click to silence removal digest for this client'}
                              aria-label={c.reminders_silenced ? 'Un-silence reminders' : 'Silence reminders'}
                              data-testid={`tw-bell-${c.id}`}
                            >
                              {c.reminders_silenced
                                ? <BellOff className="w-3.5 h-3.5" />
                                : <Bell className="w-3.5 h-3.5" />}
                            </button>
                            <span
                              className={`font-semibold break-words ${c.status === 'red' ? 'text-red-300' : c.status === 'green' ? 'text-emerald-300' : 'text-zinc-300'}`}
                              data-testid={`tw-client-name-${c.id}`}
                            >{c.client_name}</span>
                          </div>
                          {editingActiveId === c.id ? (
                            <div className="flex items-center gap-1">
                              <Input
                                type="date"
                                value={editingDate}
                                onChange={(e) => setEditingDate(e.target.value)}
                                className="bg-zinc-900 border-zinc-700 text-white h-7 text-xs w-36"
                                data-testid={`tw-edit-date-${c.id}`}
                              />
                              <Button onClick={() => saveEditedDate(c.id)} size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-7 px-2" data-testid={`tw-edit-save-${c.id}`}><CheckCircle2 className="w-3 h-3" /></Button>
                              <Button onClick={() => { setEditingActiveId(null); setEditingDate(''); }} size="sm" variant="outline" className="border-zinc-700 h-7 px-2"><X className="w-3 h-3" /></Button>
                            </div>
                          ) : (
                            <button
                              onClick={() => { setEditingActiveId(c.id); setEditingDate(c.add_date || ''); }}
                              className="text-[11px] text-zinc-500 hover:text-zinc-300 underline-offset-2 hover:underline flex items-center gap-1 text-left"
                              data-testid={`tw-edit-trigger-${c.id}`}
                            >
                              <Calendar className="w-3 h-3 flex-shrink-0" />
                              <span>{c.add_date ? `added ${formatDateUS(c.add_date)} · removes ${formatDateUS(c.removal_date)}` : 'click to set date'}</span>
                            </button>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap sm:flex-shrink-0">
                          {statusBadge(c.status, c.status === 'green' ? c.days_until_removal : (c.status === 'red' ? -c.days_until_removal : null), c.manual_removal)}
                          <button
                            onClick={() => toggleManualRemoval(c.id, c.manual_removal)}
                            className={`text-[10px] px-1.5 py-0.5 rounded border transition-colors flex-shrink-0 ${c.manual_removal ? 'border-red-500/50 text-red-300 hover:bg-red-900/30' : 'border-zinc-700 text-zinc-500 hover:text-amber-300 hover:border-amber-700'}`}
                            title={c.manual_removal ? 'Currently flagged — click to clear' : 'Mark as needs to be removed'}
                            data-testid={`tw-toggle-manual-${c.id}`}
                          >
                            {c.manual_removal ? '✓ flagged' : 'flag'}
                          </button>
                          {c.email && <span title={c.email} className="text-zinc-500 hover:text-blue-400 flex-shrink-0"><Mail className="w-3.5 h-3.5" /></span>}
                          <button
                            onClick={() => deleteActive(c.id, c.client_name)}
                            className="text-zinc-500 hover:text-red-400 flex-shrink-0"
                            data-testid={`tw-delete-${c.id}`}
                            title="Remove from log (after they're off the card)"
                          ><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              );
            })}
          </div>
        )}
      </main>

      {/* ── Pipeline paste modal ── */}
      {pipelinePasteOpen && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setPipelinePasteOpen(false)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl max-w-2xl w-full p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-2">Paste pipeline clients</h3>
            <p className="text-xs text-zinc-500 mb-3">One client per line. Each line can contain: full name, DOB (MM/DD/YYYY or MM-DD-YYYY), 9-digit SSN (we keep only the last 4), email. Order doesn't matter — we'll parse it.</p>
            <Textarea
              value={pipelinePasteText}
              onChange={(e) => setPipelinePasteText(e.target.value)}
              rows={10}
              placeholder={`Jane Smith 09/06/2000 jane@example.com 555-12-3456\nJohn Doe 12-15-1985 johndoe@gmail.com`}
              className="bg-zinc-950 border-zinc-700 text-white font-mono text-xs"
              data-testid="tw-pipeline-paste-textarea"
            />
            {pipelinePreview && (
              <div className="mt-3 bg-zinc-800 border border-zinc-700 rounded-lg p-3 max-h-60 overflow-y-auto">
                <p className="text-xs text-zinc-400 mb-2">Preview ({pipelinePreview.count} client{pipelinePreview.count !== 1 ? 's' : ''}):</p>
                {pipelinePreview.preview.map((c, i) => (
                  <div key={i} className="text-xs grid grid-cols-[1fr_100px_90px_1fr] gap-2 py-1 border-t border-zinc-700/40">
                    <span className="text-white font-medium">{c.full_name}</span>
                    <span className="text-zinc-400">{formatDateUS(c.dob) || '—'}</span>
                    <span className="text-zinc-400 font-mono">{c.ssn_last4 ? `***-${c.ssn_last4}` : '—'}</span>
                    <span className="text-zinc-400 truncate">{c.email || '—'}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <Button onClick={() => setPipelinePasteOpen(false)} variant="outline" className="border-zinc-700">Cancel</Button>
              {!pipelinePreview && <Button onClick={previewPipelinePaste} className="bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="tw-pipeline-paste-preview">Preview</Button>}
              {pipelinePreview && <Button onClick={confirmPipelinePaste} className="bg-emerald-600 hover:bg-emerald-700" data-testid="tw-pipeline-paste-confirm">Save {pipelinePreview.count} client(s)</Button>}
            </div>
          </div>
        </div>
      )}

      {/* ── Active paste modal ── */}
      {activePasteOpen && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setActivePasteOpen(false)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl max-w-3xl w-full p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-2">Paste tradeline list</h3>
            <p className="text-xs text-zinc-500 mb-3">Card names go on their own line (e.g. <code>MACYS</code>, <code>BMW</code>, <code>Wells Fargo Active Cash</code>). Clients go below each card, one per line, with optional date. Year defaults to {new Date().getFullYear()}.</p>
            <Textarea
              value={activePasteText}
              onChange={(e) => setActivePasteText(e.target.value)}
              rows={14}
              placeholder={`MACYS\nADAM WILLIAMS 02/16\nGABRIEL HULMANN 02/16\n\nBMW\nAnthony Jones 6/25\nFREDRICK THOMAS 6/24`}
              className="bg-zinc-950 border-zinc-700 text-white font-mono text-xs"
              data-testid="tw-active-paste-textarea"
            />
            {activePastePreview && (
              <div className="mt-3 bg-zinc-800 border border-zinc-700 rounded-lg p-3 max-h-72 overflow-y-auto">
                <p className="text-xs text-zinc-400 mb-2">Preview ({activePastePreview.client_count} client{activePastePreview.client_count !== 1 ? 's' : ''} across {activePastePreview.card_count} card{activePastePreview.card_count !== 1 ? 's' : ''}):</p>
                {activePastePreview.preview.map((b, i) => (
                  <div key={i} className="mt-2">
                    <p className="text-xs font-bold text-fuchsia-300">{b.card_name} {b.is_new && <span className="text-[10px] text-amber-400">(new card)</span>}</p>
                    {b.clients.map((c, j) => (
                      <div key={j} className="text-xs text-zinc-300 pl-3 py-0.5">• {c.client_name} {c.add_date ? <span className="text-zinc-500">— {formatDateUS(c.add_date)}</span> : <span className="text-zinc-600 italic">(no date)</span>}</div>
                    ))}
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <Button onClick={() => setActivePasteOpen(false)} variant="outline" className="border-zinc-700">Cancel</Button>
              {!activePastePreview && <Button onClick={previewActivePaste} className="bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="tw-active-paste-preview">Preview</Button>}
              {activePastePreview && <Button onClick={confirmActivePaste} className="bg-emerald-600 hover:bg-emerald-700" data-testid="tw-active-paste-confirm">Save</Button>}
            </div>
          </div>
        </div>
      )}

      {/* ── Add card modal ── */}
      {addCardOpen && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setAddCardOpen(false)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-3">Add a tradeline card</h3>
            <Input
              value={newCardName}
              onChange={(e) => setNewCardName(e.target.value)}
              placeholder="e.g. Chase Sapphire"
              className="bg-zinc-800 border-zinc-700 text-white"
              data-testid="tw-add-card-input"
              autoFocus
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button onClick={() => setAddCardOpen(false)} variant="outline" className="border-zinc-700">Cancel</Button>
              <Button onClick={addCard} className="bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="tw-add-card-confirm">Add</Button>
            </div>
          </div>
        </div>
      )}

      {loading && <div className="fixed top-2 right-2 bg-zinc-800 border border-zinc-700 rounded-full px-3 py-1.5 text-xs flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>}
    </div>
  );
}
